/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { STACK_GAP, StackView, STACK_DIRECTION, SPACE_FILL, STACK_ALIGN, NestedNavigation } from '@veeam/components';
import React from 'react';
import { Outlet, useLocation, useMatch, useNavigate, useOutletContext } from 'react-router-dom';

import { capitalize } from '../../../helpers/string-helpers';
import { useGlobalAppData, useGlobalAppStorage, useGlobalLang, useGlobalServices } from '../../providers/GlobalContextProvider';
import { NotificationDialogsTextKeys } from '../../../services/notification';

import type { AppRouteItem } from '../../../stores/router';

export interface NestedTabBarLayoutProps {
    currentPath: string;
    items: AppRouteItem[];
    keepQuery?: boolean;
}

export const NestedTabBarLayout: React.FC<NestedTabBarLayoutProps> = ({ currentPath, items, keepQuery = true }) => {
    const globalAppData = useGlobalAppData();
    const { notificationService } = useGlobalServices();
    const appStorage = useGlobalAppStorage();
    const location = useLocation();
    const navigate = useNavigate();
    const outletContext = useOutletContext<any>();
    const lang = useGlobalLang<Record<string, string>>();
    const match = useMatch({ path: currentPath, end: false });
    const currentPathFromParent = match?.pathname ?? currentPath;
    const pathFromStorage = appStorage?.getItem(currentPathFromParent);
    const nestedTabItems = items.filter(({ hidden }) => !hidden);
    const pathFromLocation = nestedTabItems.find(tab => location.pathname.includes(`${currentPathFromParent}/${tab.path}`))?.path ?? null;
    const pathFromState = nestedTabItems.find(tab => pathFromStorage && tab.path === pathFromStorage)?.path ?? null;
    const currentNestedTab = pathFromLocation ?? pathFromState ?? nestedTabItems[0].path;
    const currentNestedTabItems = nestedTabItems.map(({ path, navName, navNameKey, navClick }) => ({
        title: navName ?? capitalize(lang[navNameKey]),
        active: path === currentNestedTab,
        onClick: () => {
            const handleNavigate = () => {
                navClick?.();
                globalAppData.navigationBlockerPrompt = null;
                navigate(`${path}${keepQuery ? window.location.search : ''}`);
            };

            if (globalAppData.navigationBlockerPrompt) {
                notificationService.confirm(globalAppData.navigationBlockerPrompt.title, globalAppData.navigationBlockerPrompt.message)
                    .then((btn) => {
                        if (btn === NotificationDialogsTextKeys.Yes) {
                            handleNavigate();
                        }
                    });
            } else {
                handleNavigate();
            }
        },
    }));

    if (pathFromStorage !== currentNestedTab) {
        appStorage?.setItem(currentPathFromParent, currentNestedTab);
    }

    return (
        <StackView
            gap={STACK_GAP.s}
            direction={STACK_DIRECTION.column}
            spaceFill={SPACE_FILL.all}
            style={{
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {currentNestedTabItems.length > 1 && (
                <StackView align={STACK_ALIGN.start}>
                    <NestedNavigation items={currentNestedTabItems} />
                </StackView>
            )}

            <Outlet
                context={outletContext}
                key={currentPath}
            />
        </StackView>
    );
};
